import React, { ComponentProps } from "react";
import { Platform } from "react-native";
import { ParamListBase } from "@react-navigation/native";
import {
    createNativeStackNavigator,
    NativeStackNavigationOptions,
} from "@react-navigation/native-stack";
import {
    createStackNavigator,
    StackNavigationOptions,
    TransitionPreset,
    TransitionPresets,
} from "@react-navigation/stack";

/**
 * Options accepted on BOTH stack engines. It is the intersection of the native-stack and JS-stack
 * option types, so shared keys (`headerShown`, `title`, `gestureEnabled`, `presentation`, `animation`,
 * custom `header`, ...) are strongly typed while engine-specific keys go through the dedicated buckets
 * (`nativeScreenOptions` / `webScreenOptions`).
 */
export type PlatformStackScreenOptions = NativeStackNavigationOptions & StackNavigationOptions;

type WebNavigatorProps = ComponentProps<ReturnType<typeof createStackNavigator>["Navigator"]>;

/**
 * Base navigator props common to whichever engine is used at runtime. The JS-stack props are a
 * superset of the native-stack ones (native-stack adds no extra navigator config, the JS stack adds
 * `detachInactiveScreens`), so JS-stack-only props stay optional and are simply ignored by
 * native-stack at runtime.
 */
type BaseNavigatorProps = Partial<Omit<WebNavigatorProps, "screenOptions">>;

export type PlatformStackNavigatorProps = BaseNavigatorProps & {
    children: React.ReactNode;
    /** Options applied on both engines (native-stack on native, JS-stack on web). */
    screenOptions?: PlatformStackScreenOptions;
    /** Extra options merged in ONLY on native (`@react-navigation/native-stack`). */
    nativeScreenOptions?: NativeStackNavigationOptions;
    /**
     * Extra options merged in ONLY on web (`@react-navigation/stack`). Overrides the web transition
     * preset when they share keys.
     */
    webScreenOptions?: StackNavigationOptions;
};

export type CreatePlatformStackConfig = {
    /**
     * Transition preset spread into the web (JS-stack) navigator's `screenOptions`. Native uses
     * `createNativeStackNavigator`, which drives its own platform animations and ignores this.
     * Defaults to `TransitionPresets.DefaultTransition`.
     */
    webTransitionPreset?: TransitionPreset;
};

/**
 * Unified navigator surface. `Navigator` is strongly typed on {@link PlatformStackNavigatorProps};
 * `Screen` / `Group` reuse the native-stack types as the canonical shape (per-screen `options` that
 * are engine-specific may need a `PlatformStackScreenOptions` cast).
 */
export type PlatformStackNavigator<ParamList extends ParamListBase> = {
    Navigator: React.ComponentType<PlatformStackNavigatorProps>;
    Screen: ReturnType<typeof createNativeStackNavigator<ParamList>>["Screen"];
    Group: ReturnType<typeof createNativeStackNavigator<ParamList>>["Group"];
};

/**
 * Creates a stack navigator that always uses the right engine per platform:
 * - Native (iOS/Android): `@react-navigation/native-stack` (native transitions).
 * - Web: `@react-navigation/stack`, since native-stack does not animate on react-native-web. The
 *   `webTransitionPreset` (default `TransitionPresets.DefaultTransition`) is spread into its
 *   `screenOptions`.
 *
 * The returned object exposes the usual `Navigator` / `Screen` / `Group`. Pass options valid on both
 * engines via `screenOptions`, and engine-specific options via `nativeScreenOptions` /
 * `webScreenOptions`.
 */
export function createPlatformStackNavigator<ParamList extends ParamListBase>(
    config: CreatePlatformStackConfig = {},
): PlatformStackNavigator<ParamList> {
    const { webTransitionPreset = TransitionPresets.DefaultTransition } = config;

    if (Platform.OS === "web") {
        const WebStack = createStackNavigator<ParamList>();
        const Navigator = ({
            screenOptions,
            webScreenOptions,
            nativeScreenOptions: _nativeScreenOptions,
            ...rest
        }: PlatformStackNavigatorProps) => (
            <WebStack.Navigator
                {...rest}
                screenOptions={{
                    ...webTransitionPreset,
                    ...screenOptions,
                    ...webScreenOptions,
                }}
            />
        );
        // WebStack's Screen/Group are the same surface but typed against @react-navigation/stack;
        // cast them to the native-stack canonical shape the return type uses. This is the only cast
        // the runtime platform branch requires (the native branch below returns the exact type).
        return {
            Navigator,
            Screen: WebStack.Screen,
            Group: WebStack.Group,
        } as unknown as PlatformStackNavigator<ParamList>;
    }

    const NativeStack = createNativeStackNavigator<ParamList>();
    const Navigator = ({
        screenOptions,
        nativeScreenOptions,
        webScreenOptions: _webScreenOptions,
        ...rest
    }: PlatformStackNavigatorProps) => (
        <NativeStack.Navigator
            {...rest}
            screenOptions={{
                ...screenOptions,
                ...nativeScreenOptions,
            }}
        />
    );
    return {
        Navigator,
        Screen: NativeStack.Screen,
        Group: NativeStack.Group,
    };
}
